import React from 'react';
import type { StreamChat } from 'stream-chat';
import { useUserStore } from 'store/userStore';
import { useChatConnect } from 'views/Chat/hooks/useChatConnect';

type ChatClientContextValue = {
  chatClient: StreamChat | null;
  requestConnection: () => void;
};

const ChatClientContext = React.createContext<ChatClientContextValue | null>(
  null
);

/**
 * One Stream connection per signed-in session. Both the /chat page and the
 * assistant's member-chat window read it from here — a second `connectUser`
 * for the same user opens a second websocket, which doubles every event and
 * lets the two views race each other's read state.
 *
 * Nothing connects until a consumer asks; most of the backoffice never opens a
 * chat at all. Once connected it stays up for the session, so moving between
 * /chat and the assistant window doesn't churn the socket.
 */
export const ChatClientProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const chatUser = useUserStore((state) => state.chatUser);
  const [isRequested, setIsRequested] = React.useState(false);
  const chatClient = useChatConnect(isRequested ? chatUser : null);

  const requestConnection = React.useCallback(() => setIsRequested(true), []);

  const value = React.useMemo(
    () => ({ chatClient, requestConnection }),
    [chatClient, requestConnection]
  );

  return (
    <ChatClientContext.Provider value={value}>
      {children}
    </ChatClientContext.Provider>
  );
};

/** Asks for the shared connection and returns it once it is up. */
export const useChatClient = () => {
  const context = React.useContext(ChatClientContext);
  const requestConnection = context?.requestConnection;

  React.useEffect(() => {
    requestConnection?.();
  }, [requestConnection]);

  return context?.chatClient ?? null;
};
